import { DefaultQueryParams } from '@frontend/api-utils';
import { ChartClient } from '@frontend/chart/api';
import { PudoTransactionVolumeByStateListChart } from '@frontend/chart/components';
import { Chart, ChartQueryParam } from '@frontend/chart/types';
import { TransactionStateName } from '@frontend/transaction/types';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getLast30DaysChartRange } from '../last-30-days-chart-range';
import { Last30DaysPudoTransactionVolumeByStateListChartProps } from './last-30-days-pudo-transaction-volume-by-state-list-chart.component';

export const useLast30DaysPudoTransactionVolumeByStateListChart = (props: Last30DaysPudoTransactionVolumeByStateListChartProps) => {
    const navigate = useNavigate();
    const [data, setData] = useState<Chart | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!props.accountId) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        const { after, before } = getLast30DaysChartRange();

        ChartClient.fetchChartData(props.accountId, PudoTransactionVolumeByStateListChart.definition, {
            [DefaultQueryParams.AFTER]: after,
            [DefaultQueryParams.BEFORE]: before,
            [ChartQueryParam.STATE]: [
                TransactionStateName.TRANSACTION_COMPLETED,
                TransactionStateName.TRANSACTION_CANCELLED,
                TransactionStateName.TRANSACTION_FAILED
            ]
        })
            .then((chart) => {
                if (!cancelled) {
                    setData(chart);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setData(undefined);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [props.accountId]);

    const getNavigateTarget = (key: TransactionStateName) => {
        switch (key) {
            case TransactionStateName.TRANSACTION_DROP_OFF_PENDING:
                return '../awaiting_drop_off';
            case TransactionStateName.TRANSACTION_PICK_UP_PENDING:
                return '../awaiting_pick_up';
            case TransactionStateName.TRANSACTION_FAILED:
                return '../transactions?tab=failed';
            case TransactionStateName.TRANSACTION_COMPLETED:
                return '../transactions?tab=completed';
            default:
                return '../transactions';
        }
    };

    const sortedData = useMemo(() => {
        if (!data) return undefined;
        const order: Record<string, number> = {
            [TransactionStateName.TRANSACTION_COMPLETED]: 0,
            [TransactionStateName.TRANSACTION_FAILED]: 1,
            [TransactionStateName.TRANSACTION_CANCELLED]: 2
        };
        return {
            ...data,
            series: [...data.series].sort((a, b) => (order[a.name ?? ''] ?? 99) - (order[b.name ?? ''] ?? 99))
        };
    }, [data]);

    return { data: sortedData, isLoading, getNavigateTarget, navigate };
};
